import { Link } from "react-router-dom"
import { useState } from "react"
import {
  Search,
  ArrowRight,
  MapPin,
  Play,
  Star,
  User,
  ChevronDown,
  Quote,
} from "lucide-react"
import HealthArticlesWidget from "./widgets/healtharticles"
import Testimonialswidget from "./widgets/testimonials"

export function WomenCarePageLayout({
  title,
  heroTagline,
  heroImage,
  highlights = [],
  sectionTitle,
  description,
  readMoreContent,
  contentImage,
  doctors = [],
  testimonials = [],
  faqs = [],
  blogs = [],
  mediaItems = [],
  ctaTitle,
}) {
  const [openFaq, setOpenFaq] = useState(null)
  const [showMore, setShowMore] = useState(false)

  return (
    <div className="min-h-screen bg-white">
      <main>

        {/* HERO */}
        <section className="relative h-[250px] md:h-[600px] w-full overflow-hidden">
          <img
            src={heroImage}
            alt={sectionTitle}
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/30 to-transparent" />
          <div className="absolute bottom-14 left-0 right-0 z-10">
            <div className="container px-4 mx-auto">
              <h1 className="text-2xl font-bold text-white md:text-4xl lg:text-5xl max-w-3xl leading-tight">
                {heroTagline}
              </h1>
            </div>
          </div>
        </section>

        {/* HIGHLIGHTS */}
        <section className="relative z-20 -mt-12 mb-8">
          <div className="container px-4 mx-auto">
            <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
              {highlights.slice(0, 3).map((item, i) => (
                <div
                  key={i}
                  className="flex items-center gap-4 p-5 bg-white rounded-xl shadow-lg border"
                >
                  <div className="flex items-center justify-center w-14 h-14 rounded-full bg-red-100">
                    <item.icon className="w-7 h-7 text-[#951C25]" />
                  </div>
                  <p className="text-sm font-medium text-gray-700">
                    {item.text}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CONTENT */}
        <section className="py-10 md:py-16 bg-gray-50">
          <div className="container px-4 mx-auto">
            <div className="grid lg:grid-cols-3 gap-10">
              <div className="lg:col-span-2">
                <h2 className="mb-6 text-2xl font-bold md:text-3xl">
                  {sectionTitle}
                </h2>

                <p className="text-gray-600 leading-relaxed text-[15px]">
                  {description}
                </p>

                {readMoreContent && (
                  <>
                    {showMore && (
                      <p className="mt-4 text-gray-600 text-[15px]">
                        {readMoreContent}
                      </p>
                    )}
                    <button
                      onClick={() => setShowMore(!showMore)}
                      className="mt-3 text-[#951C25] font-semibold text-sm flex items-center gap-1"
                    >
                      {showMore ? "Show Less" : "Read More"}
                      <ArrowRight className="w-4 h-4" />
                    </button>
                  </>
                )}
              </div>

              <div className="relative h-64 overflow-hidden rounded-xl">
                <img
                  src={contentImage}
                  alt={sectionTitle}
                  className="w-full h-full object-cover"
                />
              </div>
            </div>
          </div>
        </section>

        {/* FIND A DOCTOR */}
        <section className="py-12 bg-white">
          <div className="container px-4 mx-auto">
            <h2 className="mb-2 text-2xl font-bold text-center">
              Find a Doctor
            </h2>

            {/* Search */}
            <div className="flex items-center max-w-md mx-auto mb-10 border rounded-full bg-gray-50 overflow-hidden">
              <input
                type="text"
                placeholder="Search Doctor"
                className="flex-1 px-5 py-3 text-sm bg-transparent outline-none"
              />
              <button className="w-10 h-10 bg-[#951C25] flex items-center justify-center text-white rounded-full mr-1">
                <Search className="w-4 h-4" />
              </button>
            </div>

            <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
              {doctors.map((doc, i) => (
                <div
                  key={i}
                  className="flex gap-4 p-4 border rounded-xl shadow-sm"
                >
                  <div className="w-24 h-28 overflow-hidden rounded-lg">
                    <img
                      src={doc.image}
                      alt={doc.name}
                      className="w-full h-full object-cover"
                    />
                  </div>

                  <div className="flex flex-col">
                    <h3 className="text-sm font-bold">{doc.name}</h3>
                    <p className="text-xs text-gray-500">
                      {doc.designation}
                    </p>

                    <div className="flex items-center gap-1 text-xs text-gray-400 mt-1">
                      <MapPin className="w-3 h-3" />
                      {doc.location}
                    </div>

                    <div className="flex gap-2 mt-3">
                      <Link
                        to="/book-appointment"
                        className="px-3 py-1 text-[11px] border border-[#951C25] text-[#951C25] rounded-md"
                      >
                        Online
                      </Link>
                      <Link
                        to="/book-appointment"
                        className="px-3 py-1 text-[11px] bg-[#951C25] text-white rounded-md"
                      >
                        Visit
                      </Link>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>


         {/* Testimonials */}
        <Testimonialswidget />

        {/* BLOGS */}
        
        <HealthArticlesWidget />

        {/* FAQ */}
        <section className="py-12 bg-gray-50">
          <div className="container px-4 mx-auto max-w-3xl">
            <h2 className="text-2xl font-bold text-center mb-8">
              FAQs
            </h2>

            {faqs.map((faq, i) => (
              <div key={i} className="border rounded-lg mb-3 bg-white">
                <button
                  onClick={() =>
                    setOpenFaq(openFaq === i ? null : i)
                  }
                  className="flex justify-between w-full p-4 text-left"
                >
                  <span>{faq.q}</span>
                  <ChevronDown
                    className={`w-5 h-5 ${
                      openFaq === i ? "rotate-180" : ""
                    }`}
                  />
                </button>

                {openFaq === i && (
                  <div className="px-4 pb-4 text-sm text-gray-600">
                    {faq.a}
                  </div>
                )}
              </div>
            ))}
          </div>
        </section>
      </main>
    </div>
  )
}
